const selectors = {
  swiperRoot: ".js-product-cg-swiper",
  next: ".swiper-button-next",
  prev: ".swiper-button-prev",
  pagination: ".swiper-pagination",
  config: "[data-product-cg-config]",
};

class ProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiperRoot = null;
    this.swiper = null;

    this._isInitialized = false;
    this._waitAttempts = 0;
    this._waitTimer = null;

    this._mql = null;
    this._onMqlChange = null;
  }

  connectedCallback() {
    this.swiperRoot = this.querySelector(selectors.swiperRoot);
    if (!this.swiperRoot) return;

    this.initWhenReady();
  }

  disconnectedCallback() {
    this.teardown();
  }

  teardown() {
    if (this._waitTimer) {
      clearTimeout(this._waitTimer);
      this._waitTimer = null;
    }

    if (this._mql && this._onMqlChange) {
      // Safari compatibility (removeEventListener may not exist on older versions)
      if (this._mql.removeEventListener) this._mql.removeEventListener("change", this._onMqlChange);
      else this._mql.removeListener(this._onMqlChange);
    }
    this._mql = null;
    this._onMqlChange = null;

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this._isInitialized = false;
    this._waitAttempts = 0;
  }

  getSlideCount() {
    const wrapper = this.swiperRoot?.querySelector(".swiper-wrapper");
    if (!wrapper) return 0;
    return wrapper.querySelectorAll(":scope > .swiper-slide").length;
  }

  initWhenReady() {
    if (this._isInitialized) return;

    if (typeof window.Swiper !== "function") {
      this._waitAttempts += 1;
      if (this._waitAttempts > 100) return;

      this._waitTimer = setTimeout(() => {
        this._waitTimer = null;
        if (!this.isConnected) return;
        this.initWhenReady();
      }, 50);

      return;
    }

    this._isInitialized = true;
    this.init();
  }

  init() {
    if (typeof window.Swiper !== "function") return;

    this._mql = window.matchMedia("(min-width: 1024px)");

    this._onMqlChange = () => {
      if (!this.isConnected) return;

      if (this._mql.matches) {
        // Desktop -> fully destroy swiper
        this.destroySwiper();
        return;
      }

      // Below 1024 -> ensure swiper exists and update behavior
      this.ensureSwiper();
      this.updateSwiperMode();
    };

    if (this._mql.addEventListener) {
      this._mql.addEventListener("change", this._onMqlChange);
    } else {
      this._mql.addListener(this._onMqlChange);
    }

    this._onResize = () => this._onMqlChange();
    window.addEventListener("resize", this._onResize, { passive: true });

    this._onMqlChange();
  }

  ensureSwiper() {
    if (this.swiper) return;

    this.swiper = new window.Swiper(this.swiperRoot, {
      slidesPerView: 2.2,
      slidesPerGroup: 1,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      loop: true,
      initialSlide: 1,
      spaceBetween: 12,
      speed: 900,
      watchOverflow: true,
      centeredSlides: true,
      a11y: { enabled: true },
      breakpoints: {
        768: { slidesPerView: 3.5, spaceBetween: 20 },
      },
    });
  }

  updateSwiperMode() {
    if (!this.swiper) return;

    const count = this.getSlideCount();
    const shouldLoop = count >= 5;

    const loopChanged = this.swiper.params.loop !== shouldLoop;

    if (!loopChanged) return;

    if (this.swiper.params.loop) this.swiper.loopDestroy();

    this.swiper.params.loop = shouldLoop;
    this.swiper.params.centeredSlides = shouldLoop;
    this.swiper.params.initialSlide = shouldLoop ? 1 : 0;

    if (shouldLoop) {
      this.swiper.params.slidesOffsetBefore = 0;
      this.swiper.params.slidesOffsetAfter = 0;
    } else {
      this.swiper.params.slidesOffsetBefore = window.matchMedia("(min-width: 768px)").matches ? 20 : 12;
      this.swiper.params.slidesOffsetAfter = window.matchMedia("(min-width: 768px)").matches ? 20 : 12;
    }

    this.swiper.params.breakpoints = {
      768: {
        spaceBetween: 20,
        slidesPerView: 3.5,
        slidesOffsetBefore: shouldLoop ? 0 : 20,
        slidesOffsetAfter: shouldLoop ? 0 : 20,
      },
    };

    if (shouldLoop) this.swiper.loopCreate();

    if (typeof this.swiper.setBreakpoint === "function") this.swiper.setBreakpoint();
    this.swiper.update();

    if (shouldLoop) this.swiper.slideToLoop(1, 0);
    else this.swiper.slideTo(0, 0);
  }

  destroySwiper() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

if (!customElements.get("product-carousel")) {
  customElements.define("product-carousel", ProductCarousel);
}
